import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HK_DOTS, HK_VIEW, projectHongKong } from "./hongKongDots";
import { useLocale } from "../i18n/useLocale";
import cases from "../data/cases";

const DOTS = HK_DOTS.split(" ").map((pair) => pair.split(",").map(Number));
const LETTERS = "virpluz";
const LETTER_STEP = 3;
const GLYPH = 128;

function placedLetters(step) {
  const bins = new Map();
  DOTS.forEach(([x, y]) => {
    const column = Math.floor(x / step);
    const row = Math.floor(y / step);
    const key = row * 1000 + column;
    const bin = bins.get(key);
    if (bin) {
      bin.x += x;
      bin.y += y;
      bin.n += 1;
    } else {
      bins.set(key, { x, y, n: 1, row, column });
    }
  });
  return [...bins.values()]
    .sort((a, b) => a.row - b.row || a.column - b.column)
    .map((bin) => [bin.x / bin.n, bin.y / bin.n]);
}

const LETTER_DOTS = placedLetters(LETTER_STEP);
const INK = "#292d29";
const HARBOUR = { x: (36 / 62) * HK_VIEW.w, y: (37 / 45) * HK_VIEW.h };
const GEO = 62 / HK_VIEW.w;

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function letterAtlas() {
  const font = getComputedStyle(document.documentElement).getPropertyValue("--serif").trim() || "serif";
  const canvas = document.createElement("canvas");
  canvas.width = GLYPH * LETTERS.length;
  canvas.height = GLYPH;
  const context = canvas.getContext("2d");
  context.clearRect(0, 0, canvas.width, canvas.height);
  context.fillStyle = INK;
  context.font = `600 ${Math.round(GLYPH * 0.72)}px ${font}`;
  context.textAlign = "center";
  context.textBaseline = "middle";
  for (let index = 0; index < LETTERS.length; index += 1) {
    context.fillText(LETTERS[index], index * GLYPH + GLYPH / 2, GLYPH * 0.56);
  }
  return canvas;
}

function Teardrop() {
  return (
    <svg viewBox="0 0 32 46" aria-hidden="true">
      <path
        d="M16 44C16 44 2 28.5 2 17.5 2 9 8.3 2.2 16 2.2S30 9 30 17.5C30 28.5 16 44 16 44Z"
        fill="var(--paper)"
      />
      <path
        d="M16 41.2C16 41.2 4.2 27.4 4.2 17.4 4.2 10.2 9.6 4.4 16 4.4S27.8 10.2 27.8 17.4C27.8 27.4 16 41.2 16 41.2Z"
        fill="currentColor"
      />
      <circle cx="16" cy="17.2" r="4.3" fill="var(--paper)" />
    </svg>
  );
}

const DottedHongKong = ({ label }) => {
  const canvasRef = useRef(null);
  const figureRef = useRef(null);
  const { t } = useTranslation();
  const { path } = useLocale();
  const [openId, setOpenId] = useState(null);
  const places = cases.map((item) => {
    const { x, y } = projectHongKong(item.lon, item.lat);
    return {
      id: item.id,
      productId: item.product,
      name: t(`home.cases.${item.id}.name`),
      address: t(`home.cases.${item.id}.address`),
      product: t(`products.items.${item.product}.name`),
      left: `${((x + 0.5) / HK_VIEW.w) * 100}%`,
      top: `${((y + 0.5) / HK_VIEW.h) * 100}%`,
    };
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    let atlas = letterAtlas();
    let frame = 0;
    let running = true;
    let fitting = false;

    const draw = (time) => {
      if (!running) return;
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      const width = Math.max(1, Math.round(canvas.clientWidth * ratio));
      const height = Math.max(1, Math.round(canvas.clientHeight * ratio));
      if (canvas.width !== width || canvas.height !== height) {
        fitting = true;
        canvas.width = width;
        canvas.height = height;
        fitting = false;
      }

      const reduced = prefersReducedMotion();
      const seconds = reduced ? 0 : time / 1000;

      const base = width / HK_VIEW.w;
      const scale = base;
      const anchorX = (HARBOUR.x + 0.5) * base;
      const anchorY = (HARBOUR.y + 0.5) * base;

      context.clearRect(0, 0, width, height);
      context.imageSmoothingEnabled = true;

      const size = scale * LETTER_STEP * 0.92;
      const half = size / 2;
      for (let index = 0; index < LETTER_DOTS.length; index += 1) {
        const [x, y] = LETTER_DOTS[index];
        const sweep = (x / HK_VIEW.w) * Math.PI * 2 - seconds * 1.35;
        const alpha = reduced ? 0.9 : 0.18 + 0.82 * (0.5 + 0.5 * Math.sin(sweep));
        const driftX = reduced ? 0 : Math.sin(seconds * 0.9 + y * 0.5 * GEO) * (0.22 / GEO);
        const driftY = reduced ? 0 : Math.cos(seconds * 0.7 + x * 0.35 * GEO) * (0.18 / GEO);
        const sx = anchorX + (x + driftX - HARBOUR.x) * scale;
        const sy = anchorY + (y + driftY - HARBOUR.y) * scale;
        if (sx < -size || sy < -size || sx > width + size || sy > height + size) continue;
        context.globalAlpha = alpha;
        context.drawImage(atlas, (index % LETTERS.length) * GLYPH, 0, GLYPH, GLYPH, sx - half, sy - half, size, size);
      }

      context.globalAlpha = 1;
      if (!reduced) frame = window.requestAnimationFrame(draw);
    };

    const kick = () => {
      window.cancelAnimationFrame(frame);
      draw(performance.now());
    };

    const onMotion = () => kick();
    let alive = true;
    const refreshLetters = () => {
      if (!alive) return;
      atlas = letterAtlas();
      kick();
    };
    document.fonts?.ready?.then(refreshLetters);
    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const resize = new ResizeObserver(() => {
      if (!fitting) kick();
    });
    motion.addEventListener("change", onMotion);
    resize.observe(canvas);
    kick();

    return () => {
      running = false;
      alive = false;
      window.cancelAnimationFrame(frame);
      motion.removeEventListener("change", onMotion);
      resize.disconnect();
    };
  }, []);

  useEffect(() => {
    const figure = figureRef.current;
    if (!figure) return undefined;
    let frame = 0;
    let natural = 0;

    const measure = () => {
      const held = figure.style.height;
      figure.style.height = "auto";
      natural = figure.getBoundingClientRect().height;
      figure.style.height = held;
    };

    const apply = () => {
      frame = 0;
      if (!natural) measure();
      if (prefersReducedMotion()) {
        figure.style.height = "";
        figure.style.setProperty("--hk-scroll", "0");
        return;
      }
      const range = Math.max(window.innerHeight * 0.85, 420);
      const progress = Math.min(1, Math.max(0, window.scrollY / range));
      const presence = 1 - progress;
      figure.style.setProperty("--hk-scroll", progress.toFixed(4));
      figure.style.height = progress < 0.004 ? "" : `${natural * presence}px`;
    };

    const request = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(apply);
    };

    const onResize = () => {
      measure();
      request();
    };

    measure();
    apply();
    window.addEventListener("scroll", request, { passive: true });
    window.addEventListener("resize", onResize);
    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    motion.addEventListener("change", request);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", request);
      window.removeEventListener("resize", onResize);
      motion.removeEventListener("change", request);
    };
  }, []);

  useEffect(() => {
    if (!openId) return undefined;
    const onPointer = (event) => {
      if (event.target.closest(".hk-pin")) return;
      setOpenId(null);
    };
    const onKey = (event) => {
      if (event.key === "Escape") setOpenId(null);
    };
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [openId]);

  return (
    <figure className="hk-dots" ref={figureRef}>
      <div className="hk-stage">
        <canvas ref={canvasRef} role="img" aria-label={label} />
        <ul className="hk-pins" aria-label={t("home.casesLabel")}>
          {places.map((place) => {
            const open = openId === place.id;
            return (
              <li
                key={place.id}
                className={open ? "hk-pin is-open" : "hk-pin"}
                style={{ left: place.left, top: place.top }}
              >
                <button
                  type="button"
                  className="hk-pin-mark"
                  aria-expanded={open}
                  aria-controls={open ? `hk-case-${place.id}` : undefined}
                  aria-label={place.name}
                  onClick={() => setOpenId(open ? null : place.id)}
                >
                  <Teardrop />
                </button>
                {open ? (
                  <div id={`hk-case-${place.id}`} className="hk-pin-card">
                    <strong>{place.name}</strong>
                    <p>{place.address}</p>
                    <Link to={path(`/solutions#${place.productId}`)}>{place.product}</Link>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      </div>
    </figure>
  );
};

export default DottedHongKong;
